import React, { useContext, useEffect, useState } from "react";
import { Container, Button, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import { formatDate } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { activitycolumns } from "../helper/ambulatoryvisitconstants";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { loadCodelistsDetailId } from "api/modules/common.option";
import TruncatedText from "Components/Common/TruncatedText";

const ViewAmbulatoryVisit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const [visit, setVisit] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const studyid = Number(localStorage.getItem("activestudyid") || 1);
  const { SelectdataAmbulatoryVisit } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(id)
  });

  // Fetch options
  useEffect(() => {
    loadCodelistsDetailId(["AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS"]).then(() => {
      setOptionsLoaded(true);
    });
  }, []);

  // Breadcrumbs sync
  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: `/study/detail/ambulatory` },
      { text: "View Ambulatory Visit" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [studyid]);

  // Set visit data once fetched
  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data) {
      const visitData = (SelectdataAmbulatoryVisit.data as any)?.data.visit[0];
      const rawActivities = (SelectdataAmbulatoryVisit.data as any)?.data.activities;
      setVisit(visitData || null);
      setActivities(Array.isArray(rawActivities) ? rawActivities : (rawActivities ? [rawActivities] : []));
    }
  }, [SelectdataAmbulatoryVisit.data]);

  if (SelectdataAmbulatoryVisit.isLoading || SelectdataAmbulatoryVisit.isFetching || SelectdataAmbulatoryVisit.isPending || !optionsLoaded) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
        <FullPageLoader />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Ambulatory Visit Not Found</h4>
            <p className="text-muted">
              The Ambulatory Visit record you are looking for does not exist or has been removed.
            </p>
            <Button color="primary" onClick={() => navigate(`/study/detail/ambulatory`)} className="mt-3">
              Back to List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* Header Block exactly like IP Inventory View */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Ambulatory Visit Details</h3>
            <p className="text-muted mb-0 d-flex align-items-center flex-wrap" style={{ gap: "4px" }}>
              <span>Visit Number: {visit.visitnumber ?? id}</span>
              <span className="mx-1">|</span>
              <span>Visit Name:</span>
              <TruncatedText text={visit.visitname || "--"} maxWidth="200px" />
              <span className="mx-1">|</span>
              <span>Visit Type: {visit.visittypename}</span>
            </p>
          </div>
        </div>

        <div className="d-flex flex-column ">

          {/* Section 1: Study Visit Details */}
          <SectionCard
            icon="ri-file-list-3-line"
            title="Visit Information"
            subtitle="Define the basic details, type, category, and description for this ambulatory visit."
          >
            <Row>
              <DetailField label="Visit Name" value={visit.visitname} columns={4} truncate={true} />
              <DetailField
                label="Visit Type"
                value={visit.visittypename}
                columns={4}
              />
              <DetailField
                label="Visit Category"
                value={visit.visitcategoryname}
                columns={4}
              />
              <DetailField
                label="Visit Description"
                value={visit.visitdescription}
                columns={12}
                expandable={true}
                maxChars={200}
              />
            </Row>
          </SectionCard>

          {/* Section 2: Visit Schedule & Window */}
          <SectionCard
            icon="ri-calendar-event-line"
            title="Visit Schedule"
            subtitle="Schedule the visit by defining its date, expected time, and permitted visit window."
          >
            <Row>
              <DetailField
                label="Scheduled Date"
                value={formatDate(visit.scheduleddate)}
                columns={4}
              />
              <DetailField
                label="Window Start"
                value={visit.visitstartday != null ? `Day ${visit.visitstartday}` : null}
                columns={4}
              />
              <DetailField
                label="Window End"
                value={visit.visitendday != null ? `Day ${visit.visitendday}` : null}
                columns={4}
              />
            </Row>
          </SectionCard>

          {/* Section 3: Configured Activities */}
          <SectionCard
            icon="ri-list-check-3"
            title="Visit Activities"
            subtitle="Define the procedures, assessments, and tasks to be completed during this visit."
          >
            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.IP_INVENTORY}
              columns={activitycolumns}
              data={activities}
              loading={SelectdataAmbulatoryVisit.isLoading}
              pageSize={activities.length || 5}
              currentPage={1}
              totalRecords={activities.length}
              paginationShow={false}
              hideAction={true}
              emptyText="No activities configured for this visit."
            />
          </SectionCard>

        </div>
      </Container>
    </div>
  );
};

export default ViewAmbulatoryVisit;
